import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { ErrorBox, Loader, PageHeader } from '../../components/Common';
import { useAuthStore } from '../../store';
import type { WeeklyScheduleSlot } from '../../types';

const DAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

interface DayState {
  enabled: boolean;
  start: string;
  end: string;
}

export function MasterSchedulePage() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-schedule', user?.id],
    queryFn: () => api.masters.schedule(user!.id),
    enabled: !!user,
  });

  const [days, setDays] = useState<DayState[]>(() =>
    Array.from({ length: 7 }, () => ({ enabled: false, start: '10:00', end: '20:00' })),
  );

  useEffect(() => {
    if (!data) return;
    const next: DayState[] = Array.from({ length: 7 }, () => ({
      enabled: false,
      start: '10:00',
      end: '20:00',
    }));
    for (const slot of data.slots) {
      next[slot.day_of_week] = {
        enabled: true,
        start: slot.start_time.slice(0, 5),
        end: slot.end_time.slice(0, 5),
      };
    }
    setDays(next);
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const slots: WeeklyScheduleSlot[] = [];
      days.forEach((d, i) => {
        if (d.enabled) {
          slots.push({
            day_of_week: i,
            start_time: `${d.start}:00`,
            end_time: `${d.end}:00`,
          });
        }
      });
      return api.masters.updateSchedule(user!.id, slots);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-schedule'] });
    },
  });

  if (isLoading) return <Loader />;
  if (error) return <ErrorBox message={(error as Error).message} onRetry={() => void refetch()} />;

  return (
    <div>
      <PageHeader title="Расписание" subtitle="Включите рабочие дни и задайте часы" />
      <div className="space-y-2">
        {DAYS.map((label, i) => {
          const d = days[i];
          return (
            <div key={i} className="card flex items-center gap-3">
              <label className="flex w-14 items-center gap-2 font-semibold">
                <input
                  type="checkbox"
                  checked={d.enabled}
                  onChange={(e) =>
                    setDays((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, enabled: e.target.checked } : x)),
                    )
                  }
                />
                {label}
              </label>
              <input
                type="time"
                value={d.start}
                disabled={!d.enabled}
                onChange={(e) =>
                  setDays((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)),
                  )
                }
                className="input flex-1 disabled:opacity-40"
              />
              <span className="text-tg-hint">—</span>
              <input
                type="time"
                value={d.end}
                disabled={!d.enabled}
                onChange={(e) =>
                  setDays((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)),
                  )
                }
                className="input flex-1 disabled:opacity-40"
              />
            </div>
          );
        })}
      </div>

      {save.error && <ErrorBox message={(save.error as Error).message} />}

      <button
        className="btn-primary mt-4 w-full"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Сохраняем...' : save.isSuccess ? 'Сохранено ✓' : 'Сохранить расписание'}
      </button>
    </div>
  );
}
